import React from "react";

import { Info } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";

import {
  AdministrativeCreditItem,
  AdministrativeCreditNoteItemSelector,
} from "./AdministrativeCreditNoteItemSelector";

interface DocumentDetail {
  id: number;
  lineNumber: number;
  quantity: number;
  netUnitValue: number;
  netAmount: number;
  variant: {
    description: string;
    code: string;
  };
}

export type AdministrativeMode = "fixed_amount" | "line_items";

interface AdministrativeCreditNoteInputProps {
  mode: AdministrativeMode;
  onModeChange: (mode: AdministrativeMode) => void;
  amount: number;
  onAmountChange: (amount: number) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  documentDetails?: DocumentDetail[];
  selectedItems?: AdministrativeCreditItem[];
  onItemsChange?: (items: AdministrativeCreditItem[]) => void;
  targetDetailIndex?: number;
  onTargetDetailIndexChange?: (index: number) => void;
  disabled?: boolean;
}

export const AdministrativeCreditNoteInput: React.FC<
  AdministrativeCreditNoteInputProps
> = ({
  mode,
  onModeChange,
  amount,
  onAmountChange,
  reason,
  onReasonChange,
  documentDetails = [],
  selectedItems = [],
  onItemsChange = () => {},
  targetDetailIndex = 0,
  onTargetDetailIndexChange = () => {},
  disabled = false,
}) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleAmountChange = (value: string) => {
    // Remove any non-digit characters except for the decimal point
    const cleanValue = value.replace(/[^\d]/g, "");
    const numericValue = cleanValue === "" ? 0 : Number(cleanValue);
    onAmountChange(numericValue);
  };

  const formatNumberInput = (amount: number): string => {
    return amount === 0 ? "" : amount.toString();
  };

  const getTotalFromItems = (): number => {
    return selectedItems.reduce((total, item) => {
      const detail = documentDetails.find(
        (d) => d.id === item.document_detail_id
      );
      if (!detail) return total;

      if (item.method === "quantity_removal") {
        return total + item.adjustment_amount * (item.affected_quantity || 0);
      } else {
        return total + item.adjustment_amount * detail.quantity;
      }
    }, 0);
  };

  const getDisplayAmount = (): number => {
    return mode === "line_items" ? getTotalFromItems() : amount;
  };

  // The entered amount is the NET credit; BSale adds 19% IVA on top, so the
  // document total is higher than the value typed. Surface the full breakdown
  // so the generated amount is never a surprise.
  const IVA_RATE = 0.19;
  const netAmount = getDisplayAmount();
  const ivaAmount = Math.round(netAmount * IVA_RATE);
  const totalAmount = netAmount + ivaAmount;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nota de Crédito Administrativa</CardTitle>
        <CardDescription>
          Selecciona el tipo de ajuste administrativo que deseas realizar
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Mode selector */}
          <div className="space-y-3">
            <Label>Tipo de Ajuste</Label>
            <RadioGroup
              value={mode}
              onValueChange={(value) =>
                onModeChange(value as AdministrativeMode)
              }
              disabled={disabled}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fixed_amount" id="fixed_amount" />
                <Label htmlFor="fixed_amount" className="cursor-pointer">
                  Monto fijo
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="line_items" id="line_items" />
                <Label htmlFor="line_items" className="cursor-pointer">
                  Ajustes por producto
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Fixed amount mode */}
          {mode === "fixed_amount" && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="admin-amount">
                  Monto neto de la Nota de Crédito
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 transform text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="admin-amount"
                    type="text"
                    value={formatNumberInput(amount)}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="0"
                    className="pl-8"
                    disabled={disabled}
                  />
                </div>
                {amount > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Neto {formatCurrency(netAmount)} + IVA 19%{" "}
                    {formatCurrency(ivaAmount)} ={" "}
                    <span className="font-medium text-foreground">
                      Total {formatCurrency(totalAmount)}
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Ingresa el monto neto; se añadirá 19% de IVA en el
                    documento.
                  </p>
                )}
              </div>

              {documentDetails.length > 1 && (
                <div className="space-y-2">
                  <Label htmlFor="admin-target-detail">
                    Producto a asociar
                  </Label>
                  <Select
                    value={String(targetDetailIndex)}
                    onValueChange={(value) =>
                      onTargetDetailIndexChange(Number(value))
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger id="admin-target-detail" className="h-9">
                      <SelectValue placeholder="Selecciona un producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {documentDetails.map((detail, index) => (
                        <SelectItem key={detail.id} value={String(index)}>
                          {detail.variant?.description || `Ítem ${index + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    La nota de crédito se emitirá sobre esta línea de la
                    factura. El monto y los totales no cambian según el producto
                    elegido.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="admin-reason">
                  Razón Administrativa <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="admin-reason"
                  value={reason}
                  onChange={(e) => onReasonChange(e.target.value)}
                  placeholder="Especifica la razón para esta nota de crédito administrativa (ej: Descuento por fidelidad, Ajuste por error de facturación, etc.)"
                  rows={4}
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">
                  Proporciona una explicación clara del motivo de esta nota de
                  crédito administrativa. Esta información aparecerá en el
                  documento oficial.
                </p>
              </div>
            </div>
          )}

          {/* Line items mode */}
          {mode === "line_items" && (
            <AdministrativeCreditNoteItemSelector
              documentDetails={documentDetails}
              selectedItems={selectedItems}
              onItemsChange={onItemsChange}
              reason={reason}
              onReasonChange={onReasonChange}
              disabled={disabled}
            />
          )}

          {/* Summary */}
          {getDisplayAmount() > 0 && (
            <div className="rounded-lg border bg-muted/40 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h4 className="font-medium text-foreground">
                    Resumen de Nota de Crédito Administrativa
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {mode === "fixed_amount"
                      ? "Se generará una nota de crédito administrativa por el monto especificado"
                      : `${selectedItems.length} producto(s) con ajustes administrativos`}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-foreground">
                    {formatCurrency(totalAmount)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Total · Neto {formatCurrency(netAmount)} + IVA{" "}
                    {formatCurrency(ivaAmount)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Important notes */}
          <div className="rounded-lg border bg-muted/40 p-4 text-sm">
            <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
              Importante
            </div>
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              <li>
                La nota de crédito administrativa no afecta el stock de
                productos
              </li>
              <li>
                La factura original permanece válida con su monto original
              </li>
              <li>
                Este crédito debe ser aplicado manualmente en la cuenta del
                cliente
              </li>
              <li>
                Asegúrate de que el monto y la razón sean correctos antes de
                proceder
              </li>
              {mode === "line_items" && (
                <li>
                  Los ajustes por producto permiten mayor precisión en los
                  créditos
                </li>
              )}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
